import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { SectionLoader } from "@/components/loading";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, LayoutList, TreePine, FolderOpen } from "lucide-react";
import { fetchCourseCurriculum, CurriculumApiError } from "@/lib/api/curriculum";
import { ADMIN_QUERY_KEYS, OPERATIONAL_QUERY_OPTIONS } from "@/lib/api/query-options";
import { CurriculumTree } from "@/components/admin/curriculum/CurriculumTree";
import { CurriculumTable } from "@/components/admin/curriculum/CurriculumTable";
import { CurriculumExportButton } from "@/components/admin/curriculum/CurriculumExportButton";
import { QuestionPoolDrawer } from "@/components/admin/curriculum/QuestionPoolDrawer";

export const Route = createFileRoute("/_authenticated/admin/courses_/$courseId_/curriculum")({
  component: CurriculumPage,
});

function CurriculumPage() {
  const { courseId } = Route.useParams();

  // `manage:courses` is granted only to SystemAdmin and ContentCreator. The API deliberately
  // admits a TeamLeader who instructs the course, so RequirePermission would lock them out.
  // The client cannot know which courses a TeamLeader instructs — the server answers 403 for
  // courses they don't, handled below.
  const { hasPermission, isTeamLeader } = useAuth();
  if (!hasPermission("manage:courses") && !isTeamLeader) {
    return <AccessDenied message="You do not have access to curriculum overview." />;
  }

  return <CurriculumContent courseId={courseId} />;
}

function CurriculumContent({ courseId }: { courseId: string }) {
  const [viewMode, setViewMode] = useState<"tree" | "flat">("tree");
  const [selectedExamId, setSelectedExamId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.courseCurriculum(courseId),
    queryFn: () => fetchCourseCurriculum(courseId),
    ...OPERATIONAL_QUERY_OPTIONS,
  });

  if (isLoading) return <SectionLoader label="Loading curriculum…" />;

  if (error) {
    if (error instanceof CurriculumApiError && error.status === 403) {
      return <AccessDenied message="You do not have access to this course's curriculum." />;
    }
    if (error instanceof CurriculumApiError && error.status === 404) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4 text-center">
          <h2 className="text-xl font-bold">Course not found</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            The course you are looking for does not exist or has been removed.
          </p>
          <Button variant="outline" asChild>
            <Link to="/admin/courses">Back to courses</Link>
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4 text-center">
        <h2 className="text-xl font-bold text-destructive">Something went wrong</h2>
        <p className="text-sm text-muted-foreground max-w-md">{error.message}</p>
      </div>
    );
  }

  if (!data) return null;

  const { totals, totalDurationFormatted, tracks } = data;
  const isEmpty = data.modules.length === 0 && data.rangeGates.length === 0 && !data.finalExam;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/admin/courses/$courseId"
          params={{ courseId }}
          className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to course
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="h-8 w-8" />
              {data.courseName}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {data.category && <Badge variant="secondary">{data.category}</Badge>}
              <Badge variant={data.isPublished ? "default" : "outline"}>
                {data.isPublished ? "Published" : "Draft"}
              </Badge>
              {tracks.map((t) => (
                <Badge key={t.trackId} variant="outline" className="text-xs">
                  {t.trackName}
                </Badge>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {totals.moduleCount} modules · {totals.videoGateCount} video gates ·{" "}
              {totals.rangeGateCount} range gates · {totals.totalQuestionCount} questions
              {totals.hasFinalExam ? " · Final exam" : ""} · {totalDurationFormatted}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CurriculumExportButton courseId={courseId} />
          </div>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={viewMode === "tree" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("tree")}
        >
          <TreePine className="h-4 w-4 mr-1.5" />
          Tree
        </Button>
        <Button
          variant={viewMode === "flat" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("flat")}
        >
          <LayoutList className="h-4 w-4 mr-1.5" />
          Flat
        </Button>
      </div>

      {/* Content */}
      {isEmpty ? (
        <EmptyState
          icon={FolderOpen}
          title="No curriculum content"
          description="This course has no modules, gates, or a final exam configured yet."
        />
      ) : viewMode === "tree" ? (
        <CurriculumTree data={data} onExamClick={setSelectedExamId} />
      ) : (
        <CurriculumTable data={data} onExamClick={setSelectedExamId} />
      )}

      {/* Question pool side drawer */}
      <QuestionPoolDrawer
        courseId={courseId}
        selectedExamId={selectedExamId}
        onClose={() => setSelectedExamId(null)}
      />
    </div>
  );
}
